import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  Toolbar,
  TextField,
  Typography,
  Button,
} from "@mui/material";

//hooks
import { useTenant } from "../../hooks/useTenant";
import { forgotPasswordRequest } from "../../api/api";

//components
import CustomAppBar from "../customAppBar";
import LoadingScreen from "../../shared_modules/loadingScreen";

import myVideo from "../../images/bellosvideo.mp4";

const Forgot = () => {
  const navigate = useNavigate();

  const { tenant, status } = useTenant(); //load tenent

  //form states
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAlert, setShowAlert] = useState({
    show: false,
    message: "",
    type: "",
  });

  //resend
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(false);
  const [countdown, setCountdown] = useState(120);
  const resendTimerRef = useRef(null);

  // Clear the cooldown interval on unmount so it can't fire setState afterward
  useEffect(() => {
    return () => {
      if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    };
  }, []);

  if (status === "loading") {
    return <LoadingScreen />;
  }

  const handleForgotPassword = async () => {
    setIsSubmitting(true);
    setShowAlert({ show: false, message: "", type: "" });

    try {
      await forgotPasswordRequest(email);

      handleResendTimer();

      setShowAlert({
        show: true,
        message: (
          <>
            We sent a reset password link to <strong>{email}</strong>.
            <br />
            <br />
            <span style={{ opacity: 0.75 }}>
              Don't see it? Check your <strong>spam</strong> or{" "}
              <strong>junk</strong> folder.
            </span>
          </>
        ),
        type: "success",
      });
    } catch (err) {
      setShowAlert({ show: true, message: err.message, type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  async function handleResend() {
    setResendLoading(true);

    try {
      await forgotPasswordRequest(email);

      handleResendTimer();

      setShowAlert({
        show: true,
        message: "Password reset link sent again.",
        type: "success",
      });
    } catch (err) {
      setShowAlert({ show: true, message: err.message, type: "error" });
    } finally {
      setResendLoading(false);
    }
  }

  function handleResendTimer() {
    // Start 120 second cooldown so they can't spam the button
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    setResendCooldown(true);
    setCountdown(120);

    resendTimerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(resendTimerRef.current);
          resendTimerRef.current = null;
          setResendCooldown(false);
          return 120;
        }
        return prev - 1;
      });
    }, 1000);
  }

  return (
    <>
      <CustomAppBar tenant={tenant} showLoginButton={false} />

      <Grid
        container
        sx={{ height: "calc(100vh - 64px)", minHeight: "500px", mt: "64px" }}
      >
        <Grid size={{ xs: 12, lg: 7 }}>
          <Box
            sx={{
              backgroundColor: "background.default",
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "flex-start", lg: "center" },
              flexDirection: "column",
              height: "100%",
              padding: { xs: "30px 20px", lg: "30px 50px" },
            }}
          >
            <Typography variant="h3">Forgot Password</Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mt: 1,
                mb: 4,
                fontWeight: 300,
              }}
            >
              Enter your email to request a password reset
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: "white",
                border: "1px solid",
                borderColor: "customDivider",
                padding: "30px",
                borderRadius: "24px",
                width: { xs: "100%", sm: "450px" },
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
              }}
            >
              {showAlert.show === true && (
                <Alert
                  sx={{ mb: showAlert.type === "success" ? 0 : 2 }}
                  severity={showAlert.type}
                >
                  {showAlert.message}
                </Alert>
              )}

              {showAlert.show === true && showAlert.type === "success" && (
                <Box sx={{ display: "flex", width: "100%" }}>
                  <Button
                    color="primary"
                    variant="contained"
                    disabled={resendLoading || resendCooldown}
                    sx={{ mt: 5, width: "100%" }}
                    onClick={() => {
                      handleResend();
                    }}
                  >
                    {resendCooldown
                      ? `Resend Email (${countdown}s)`
                      : resendLoading
                        ? "Sending..."
                        : "Resend password reset"}
                  </Button>
                </Box>
              )}

              {showAlert.type !== "success" && (
                <>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.primary",
                      fontWeight: 300,
                      pb: 1,
                    }}
                  >
                    Email
                  </Typography>
                  <TextField
                    variant="outlined"
                    disabled={isSubmitting}
                    label=""
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                    }}
                  />

                  <Button
                    color="primary"
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{ mt: 5 }}
                    onClick={() => {
                      handleForgotPassword();
                    }}
                  >
                    {!isSubmitting && "Reset Password"}
                    {isSubmitting && (
                      <CircularProgress
                        enableTrackSlot
                        size="30px"
                        aria-label="Loading…"
                        sx={{ color: "text.primary" }}
                      />
                    )}
                  </Button>
                </>
              )}

              <Box
                sx={{
                  display: isSubmitting ? "none" : "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mt: 3,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 300,
                  }}
                >
                  Back to
                </Typography>

                <Typography
                  variant="body2"
                  onClick={() => {
                    navigate("/login");
                  }}
                  sx={{
                    color: "text.primary",
                    fontWeight: 300,
                    ml: 1,
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                      textUnderlineOffset: "5px",
                    },
                  }}
                >
                  Login
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid
          size={5}
          sx={{
            display: { xs: "none", lg: "flex" },
            padding: "15px",
          }}
        >
          <video
            width="100%"
            height="100%"
            controls={false}
            muted={true}
            autoPlay={true}
            loop={true}
            playsInline={true}
            preload="auto"
            poster="image"
            style={{
              objectFit: "cover",
              borderRadius: "15px",
            }}
          >
            <source src={myVideo} type="video/mp4" />
          </video>
        </Grid>
      </Grid>
    </>
  );
};

export default Forgot;
